import { useState } from 'react';
import {
  chooseOption, unchooseOption, setOptionStatus, moveEvent, deleteEvent,
} from '../lib/api.js';
import EventCard, { Block } from '../components/EventCard.jsx';
import History, { HistoryLine } from '../components/History.jsx';
import SwipeToDelete from '../components/Swipe.jsx';
import Icon from '../components/Icon.jsx';
import {
  rangeLabel, dayShort, localTime, localAmount, journeyEnds, zoneTime,
  mapsHref, telHref, groupByCategory, categoryLabel, daysOfStop, isoInZone,
  mapsRouteHref, stopMaps, isMappable, MAX_ROUTE_POINTS, deletePrompt, countryName,
  bookingLine,
} from '../lib/format.js';

// A trip has two shapes and the app should show both. Time answers "what is
// happening now"; place answers "we are in Kuala Lumpur for three days, what do
// we have here" — which is how planning actually works, and where decisions
// live. Dining options belong to a city, not to a Tuesday.
//
// Membership is derived from dates rather than trusting stop_id, because events
// written by an agent before stops existed carry no stop reference. Where the
// reference is present it wins.

export function eventsForStop(stop, events, legsBy = new Map()) {
  const from = stop.arrive_date;
  const to = stop.depart_date ?? stop.arrive_date;
  const within = (d) => Boolean(d && from && d >= from && d <= to);

  return events.filter((e) => {
    if (e.parent_id) return false;                 // legs answer through their journey

    // A journey belongs to two stops at once: the one it leaves and the one it
    // lands in. stop_id cannot express that — every internal flight is filed
    // against its destination, so trusting it alone hid every departure — and
    // event_date names only the departure, so the flight that lands here on the
    // 11th is dated the 10th. Ask the journey's own ends instead, and let a
    // stop_id add to that rather than replace it.
    if (e.kind === 'transport') {
      const { departs, arrives } = journeyEnds(e, legsBy.get(e.id) ?? []);
      return e.stop_id === stop.id
        || arrives === from || departs === to
        || within(departs) || within(arrives);
    }

    if (e.stop_id) return e.stop_id === stop.id;
    return within(e.event_date);
  });
}

const isBooked = (e) =>
  Boolean(e.confirmation) || ['paid', 'prepaid', 'deposit_paid'].includes(e.payment_status);

export default function StopsView({ data, model, onDocChanged, onChange }) {
  const [openId, setOpenId] = useState(null);
  const stop = data.stops.find((s) => s.id === openId);

  if (stop) {
    return <StopDetail stop={stop} data={data} model={model} onChange={onChange}
                       onDocChanged={onDocChanged} onBack={() => setOpenId(null)} />;
  }

  // A choice that names no stop belongs to the trip. It has to appear here, or
  // it appears nowhere at all.
  const homeless = data.shortlists.filter((sl) => !sl.stop_id);
  // Same rule for what an agent writes about the trip rather than about a city:
  // it used to sit under "Note" at the foot of Info, which is where things go to
  // not be read. Trip-level knowledge belongs on the screen that describes the
  // trip.
  const tripNotes = data.blocks.filter((b) => b.parent_type === 'trip' && b.type !== 'warning');
  const warnings = data.blocks.filter((b) => b.parent_type === 'trip' && b.type === 'warning');

  // Two countries is two trips in one head. Where there is more than one, the
  // list says where the border falls; where there is one, nothing is added.
  const countries = [...new Set(data.stops.map((s) => s.country).filter(Boolean))];
  const showCountry = countries.length > 1;

  return (
    <>
    {warnings.map((b) => <Block key={b.id} block={b} />)}

    <section className="section" style={{ marginTop: 'var(--s-5)' }}>
      <div className="stack">
        {data.stops.map((s, i) => {
          const evs = eventsForStop(s, data.events, model.legsBy);
          const lodging = evs.find((e) => e.kind === 'lodging');
          const doing = evs.filter((e) => ['activity', 'meal'].includes(e.kind));
          const lists = data.shortlists.filter((sl) => sl.stop_id === s.id);
          const open = lists.reduce(
            (n, sl) => n + sl.items.filter((i) => i.status === 'candidate').length, 0);
          const conflicts = evs.filter((e) => e.decided === false).length;
          const isNow = model.today >= s.arrive_date && model.today < s.depart_date;

          const border = showCountry && s.country !== data.stops[i - 1]?.country;

          return (
            <div key={s.id}>
            {border && <p className="country">{countryName(s.country)}</p>}
            <button className={`stopcard${isNow ? ' now' : ''}`}
                    onClick={() => setOpenId(s.id)}>
              <div className="head">
                <b>{s.name}</b>
                {isNow && <span className="state ok">adesso</span>}
                <span className="when">{rangeLabel(s.arrive_date, s.depart_date)}</span>
              </div>
              {lodging && <p className="hotel"><Icon name="bed" size={13} /> {lodging.title}</p>}
              <p className="tally">
                {[
                  doing.length ? `${doing.length} in programma` : null,
                  open ? `${open} da decidere` : null,
                  conflicts ? `${conflicts} in conflitto` : null,
                  s.nights ? `${s.nights} notti` : null,
                ].filter(Boolean).join(' · ')}
              </p>
            </button>
            </div>
          );
        })}
      </div>
    </section>

    {(data.trip.history || tripNotes.length > 0) && (
      <section className="section">
        <h2>Da sapere</h2>
        <p className="lead">Sul viaggio nel suo insieme. Ogni tappa ha le sue.</p>
        <History title="Un po' di storia" text={data.trip.history} flat />
        {tripNotes.map((b) => <Block key={b.id} block={b} />)}
      </section>
    )}

    {homeless.length > 0 && (
      <section className="section">
        <h2>Scelte per tutto il viaggio</h2>
        <p className="lead">Non legate a una tappa sola.</p>
        {homeless.map((sl) => <Shortlist key={sl.id} list={sl} onChange={onChange} />)}
      </section>
    )}
    </>
  );
}

function StopDetail({ stop, data, model, onDocChanged, onChange, onBack }) {
  const evs = eventsForStop(stop, data.events, model.legsBy);
  const ends = (e) => journeyEnds(e, model.legsBy.get(e.id) ?? []);

  // Arriving and leaving are two different questions asked of the same journeys,
  // and a journey answers yes to one of them at each end of the stay.
  const moves = evs.filter((e) => e.kind === 'transport');
  const arriving = moves.filter((e) => ends(e).arrives === stop.arrive_date);
  const leaving = moves.filter((e) => ends(e).departs === stop.depart_date);
  const lodging = evs.filter((e) => e.kind === 'lodging');
  const planned = evs
    .filter((e) => ['activity', 'meal'].includes(e.kind))
    .sort((a, b) => String(a.event_date).localeCompare(String(b.event_date))
      || String(localTime(a) ?? '99:99').localeCompare(String(localTime(b) ?? '99:99')));
  const booked = planned.filter(isBooked);
  const loose = planned.filter((e) => !isBooked(e));
  const lists = data.shortlists.filter((sl) => sl.stop_id === stop.id);
  const blocks = data.blocks.filter((b) => b.parent_type === 'stop' && b.parent_id === stop.id);
  const stopZone = (lodging.find((e) => e.timezone) ?? evs.find((e) => e.timezone))?.timezone ?? null;

  // Events and options are grouped together by category. A category is the
  // question ("where do we eat"); the entries are the answers, decided or not.
  // A confirmed option and the event it produced are the same thing. Show the
  // event — it carries the time, the documents and the map — and hang the undo
  // on it, rather than leaving two cards that disagree about which is real.
  const promotedBy = new Map();
  for (const sl of lists) {
    for (const i of sl.items) {
      if (i.promoted_event_id) promotedBy.set(i.promoted_event_id, { item: i, list: sl });
    }
  }

  const entries = [
    ...planned.map((e) => ({ event: e, category: e.category, place: e.place, kind: e.kind,
                             from: promotedBy.get(e.id) ?? null })),
    ...lists.flatMap((sl) => sl.items
      .filter((i) => i.status !== 'rejected')
      // Skip an option whose event is already on this page.
      .filter((i) => !(i.promoted_event_id && planned.some((e) => e.id === i.promoted_event_id)))
      .map((i) => ({ item: i, list: sl, category: i.category ?? i.place?.category,
                     place: i.place, shortlist_kind: sl.kind }))),
  ];
  // Discarded candidates leave the categories — that is the point of discarding
  // them — but they do not leave the app. They collect at the foot of the stop,
  // folded, because a plan that falls through on the road is exactly when you
  // want the option you said no to.
  const dropped = lists.flatMap((sl) => sl.items
    .filter((i) => i.status === 'rejected')
    .map((i) => ({ item: i, list: sl })));

  const sections = groupByCategory(entries).map(([cat, list]) => [cat, list.sort((a, b) => {
    // Decided things first: they are commitments, the rest are candidates.
    const rank = (x) => (x.event ? 0 : x.item.status === 'chosen' ? 1 : 2);
    return rank(a) - rank(b)
      || String(a.event?.event_date ?? '').localeCompare(String(b.event?.event_date ?? ''));
  })]);

  return (
    <>
      <div className="stopdetail-head">
        <button className="act" onClick={onBack}>
          <Icon name="chevronLeft" size={14} /> Tutte le tappe
        </button>
        <h2>{stop.name}</h2>
        <p className="where">
          {rangeLabel(stop.arrive_date, stop.depart_date)}
          {stop.nights ? ` · ${stop.nights} notti` : ''}
          {stop.region ? ` · ${stop.region}` : ''}
        </p>
        {/* The agent has been writing this into every stop and the app has
            never shown it once: what this stop is FOR. It belongs directly
            under the name, before anything you can tap. */}
        {stop.summary && <p className="summary">{stop.summary}</p>}
        {isMappable(stop) && (
          <div className="actions">
            <a className="act" href={mapsHref(stop)} target="_blank" rel="noreferrer">
              <Icon name="pin" size={14} /> Apri la mappa
            </a>
          </div>
        )}
      </div>

      {/* Everything you might want to READ about the city, in one place, folded.
          It used to be scattered: the story under the title, «Come muoversi» in
          the head, and the agent's notes orphaned at the very bottom of the page
          under a heading called "Note" that named nothing. */}
      {(stop.history || stop.getting_around || stop.notes || blocks.length > 0) && (
        <section className="section">
          <h2>Da sapere</h2>
          <History title={`Storia di ${stop.name}`} text={stop.history} flat />
          {stop.getting_around && (
            <Block block={{ type: 'note', title: 'Come muoversi',
                            content: { text: stop.getting_around } }} />
          )}
          {stop.notes && (
            <Block block={{ type: 'note', title: 'Note', content: { text: stop.notes } }} />
          )}
          {blocks.map((b) => <Block key={b.id} block={b} />)}
        </section>
      )}

      <StopMaps stop={stop} lodging={lodging} planned={planned}
                options={entries.filter((x) => x.item).map((x) => x.item)} />

      {lodging.length > 0 && (
        <section className="section">
          <h2>Dove dormiamo</h2>
          <div className="stack">
            {lodging.map((e) => (
              <EventCard key={e.id} event={e} onDocChanged={onDocChanged} />
            ))}
          </div>
        </section>
      )}

      {(arriving.length > 0 || leaving.length > 0) && (
        <section className="section">
          <h2>Arrivo e partenza</h2>
          <div className="rows">
            {[
              ...arriving.map((e) => ({ e, way: 'arrivo' })),
              ...leaving.map((e) => ({ e, way: 'partenza' })),
            ].map(({ e, way }) => {
              const t = ends(e);
              const at = way === 'arrivo' ? t.arriveAt : t.departAt;
              const zone = way === 'arrivo' ? t.arriveZone : t.departZone;
              return (
                <div className="row" key={`${way}:${e.id}`}>
                  <span className="when" style={{ color: 'var(--muted)' }}>
                    {dayShort(way === 'arrivo' ? t.arrives : t.departs)}
                  </span>
                  <span className="body">
                    <b>{e.title}</b>
                    <small>
                      {[way, at != null ? zoneTime(at, zone) : localTime(e),
                        e.details?.number, e.provider].filter(Boolean).join(' · ')}
                    </small>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {sections.map(([cat, entries]) => (
        <CategorySection key={cat} category={cat} entries={entries} stop={stop}
                         stopZone={stopZone} onChange={onChange} onDocChanged={onDocChanged} />
      ))}

      {dropped.length > 0 && <Dropped entries={dropped} onChange={onChange} />}

      {sections.length === 0 && (
        <p className="day-empty" style={{ marginTop: 'var(--s-5)' }}>
          Ancora niente per questa tappa. Chiedi a Claude cosa vedere o dove mangiare.
        </p>
      )}
    </>
  );
}

// What was said no to, and the way back. Folded by default: it is the smallest
// possible answer to "and the ones we dropped?" without any of them competing
// for attention with the plan.
function Dropped({ entries, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  return (
    <section className="section">
      <button className="cathead" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <Icon name="chevronRight" size={15}
              style={{ rotate: open ? '90deg' : '0deg', color: 'var(--faint)' }} />
        <b>Scartati</b>
        <span className="count">{entries.length}</span>
      </button>
      {open && (
        <div className="rows">
          {entries.map(({ item: i, list: sl }) => (
            <div className="row option rejected" key={i.id}>
              <span className="body">
                <b>{i.title ?? i.place?.name}</b>
                <small>{[sl.title, i.verdict].filter(Boolean).join(' · ')}</small>
              </span>
              <span className="trail">
                <button className="act" disabled={busy === i.id}
                        onClick={async () => {
                          setBusy(i.id);
                          try { await setOptionStatus(i.id, 'candidate'); await onChange?.(); }
                          catch (e) { alert(e.message); }
                          finally { setBusy(null); }
                        }}>
                  {busy === i.id ? 'Attendi…' : 'Rimetti'}
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// Where things are, per stop and per day.
//
// Google's URL scheme cannot draw a handful of loose pins — a route through them
// is the only way to get more than one marker — so each of these opens the day
// as a route in the order it happens, starting from the hotel. That is close
// enough to the question being asked ("are these near each other, and in what
// order do we do them") to be worth the small lie of drawn lines.
function StopMaps({ stop, lodging, planned, options }) {
  const maps = stopMaps({ stop, lodging, events: planned, options });
  if (maps.length === 0) return null;

  return (
    <section className="section">
      <h2>Mappe</h2>
      <p className="lead">Si aprono in Google Maps, come percorso.</p>
      <div className="rows">
        {maps.map((m) => {
          const shown = Math.min(m.places.length, MAX_ROUTE_POINTS);
          return (
            <a className="row tappable" key={m.key} target="_blank" rel="noreferrer"
               href={mapsRouteHref(m.places)}>
              <span className="check" style={{ border: 'none', background: 'none',
                                               color: 'var(--brand)' }}>
                <Icon name="pin" size={15} />
              </span>
              <span className="body">
                <b>{m.label}</b>
                <small>
                  {[
                    `${shown} ${shown === 1 ? 'luogo' : 'luoghi'}`,
                    // Never silently truncate: a map of the first ten of
                    // fourteen places says so.
                    m.places.length > MAX_ROUTE_POINTS
                      ? `i primi ${MAX_ROUTE_POINTS} di ${m.places.length}` : null,
                    m.places.map((p) => p.name).filter(Boolean).slice(0, 3).join(' · '),
                  ].filter(Boolean).join(' · ')}
                </small>
              </span>
              <span className="trail"><Icon name="chevronRight" size={14} /></span>
            </a>
          );
        })}
      </div>
    </section>
  );
}

// A category is a question; its entries are the answers. Seventeen restaurants
// under one heading is a pile, so a section with more than a handful starts
// folded and says how much is inside — and where the answers come from more than
// one list, they stay grouped by list, because "dinner at the hotel" and "Malay
// dinner out" are different questions that happen to share a category.
const FOLD_ABOVE = 6;

function CategorySection({ category, entries, stop, stopZone, onChange, onDocChanged }) {
  const [open, setOpen] = useState(entries.length <= FOLD_ABOVE);
  const decided = entries.filter((x) => x.event || x.item?.status === 'chosen').length;

  const byList = new Map();
  for (const x of entries) {
    const key = x.list?.id ?? 'plan';
    if (!byList.has(key)) byList.set(key, { list: x.list, items: [] });
    byList.get(key).items.push(x);
  }
  const groups = [...byList.values()];
  const split = groups.length > 1;

  return (
    <section className="section">
      <button className="cathead" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <Icon name="chevronRight" size={15}
              style={{ rotate: open ? '90deg' : '0deg', color: 'var(--faint)' }} />
        <b>{categoryLabel(category)}</b>
        <span className="count">
          {entries.length}
          {decided > 0 && entries.length > decided && ` · ${decided} deciso`}
        </span>
      </button>

      {open && groups.map((g, gi) => (
        <div key={g.list?.id ?? 'plan'}>
          {split && g.list && (
            <p className="sublist">
              {g.list.title}
              {g.list.exclusive && <span className="excl"> · una sola</span>}
            </p>
          )}
          {split && !g.list && <p className="sublist">In programma</p>}
          <div className="stack" style={{ marginTop: split ? 'var(--s-2)' : 0,
                                          marginBottom: gi < groups.length - 1 ? 'var(--s-4)' : 0 }}>
            {g.items.map((x) => x.item
              ? <Option key={`o:${x.item.id}`} item={x.item} list={x.list}
                        stop={stop} stopZone={stopZone} onChange={onChange}
                        hideFrom={split} />
              : (
                <SwipeToDelete key={x.event.id} prompt={deletePrompt(x.event)}
                               onDelete={async () => {
                                 await deleteEvent(x.event);
                                 await onChange?.();
                               }}>
                  <EventCard event={x.event}
                             showDate={dayShort(x.event.event_date)}
                             fromList={x.from?.list?.title}
                             moveDays={daysOfStop(stop)} moveZone={stopZone}
                             onMove={async (patch) => {
                               await moveEvent(x.event, patch);
                               await onChange?.();
                             }}
                             onReopen={x.from ? async () => {
                               await unchooseOption(x.from.item.id);
                               await onChange?.();
                             } : undefined}
                             onDelete={async () => {
                               await deleteEvent(x.event);
                               await onChange?.();
                             }}
                             onDocChanged={onDocChanged} />
                </SwipeToDelete>
              ))}
          </div>
        </div>
      ))}
    </section>
  );
}

// A single candidate or confirmed choice, with its description and the button
// that commits to it. Confirming is the point: a shortlist you cannot act on is
// a document, not a plan.
function Option({ item: i, list: sl, stop, stopZone, onChange, hideFrom }) {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const days = daysOfStop(stop);
  // No default day at all. The first attempt fell back to the stop's arrival
  // date and put Batu Caves on the evening the flight lands at 21:50 — a
  // plausible guess that was simply wrong, and silently so. undefined means
  // "not decided yet", '' means the traveller deliberately chose no date, and
  // Conferma stays disabled until one of the two is true.
  const [day, setDay] = useState(sl?.anchor_date ?? undefined);
  const [time, setTime] = useState('');
  const chosen = i.status === 'chosen';
  const descr = i.description ?? i.place?.description;

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await onChange?.(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  return (
    <article className={`optcard${chosen ? ' chosen' : ''}`}>
      <div className="head">
        <b>{i.title ?? i.place?.name}</b>
        {chosen
          ? <span className="state ok">scelto</span>
          : <span className="state attn">da decidere</span>}
        {i.est_total_local != null && (
          <span className="cost">{localAmount(i.est_total_local, i.est_currency_local)}</span>
        )}
      </div>

      {descr && <p className="descr">{descr}</p>}
      {i.distance_note && <p className="meta">{i.distance_note}</p>}
      {i.pros?.length > 0 && <p className="pro">+ {i.pros.join(' · ')}</p>}
      {i.cons?.length > 0 && <p className="con">− {i.cons.join(' · ')}</p>}
      {i.verdict && <p className="meta">{i.verdict}</p>}
      {/* "come si prenota" is the last thing between a candidate and a booking,
          and it was written on the row and rendered nowhere. */}
      {i.reservation_note && <p className="meta">{i.reservation_note}</p>}
      {bookingLine(i.place) && <p className="meta">{bookingLine(i.place)}</p>}
      {i.place?.notes && <p className="meta">{i.place.notes}</p>}
      {i.place?.tags?.length > 0 && <p className="tags">{i.place.tags.join(' · ')}</p>}
      {i.place?.open_hours && typeof i.place.open_hours === 'object' && (
        <p className="meta">
          {Object.entries(i.place.open_hours)
            .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
            .map(([k, v]) => `${k} ${v}`).join(' · ')}
        </p>
      )}
      <HistoryLine text={i.place?.history} />

      <div className="actions">
        {(i.place?.phone || i.vendor?.whatsapp) && (
          <a className="act" href={telHref(i.place?.phone ?? i.vendor?.whatsapp)}>
            <Icon name="phone" size={13} /> Chiama
          </a>
        )}
        {/* A candidate with no coordinates still has a name and a city, and
            Google finds a temple from those. Hiding the link entirely unless
            lat/lon happened to be filled in was how a perfectly findable place
            came to look unmappable. */}
        {isMappable(i.place) && (
          <a className="act" href={mapsHref(i.place)} target="_blank" rel="noreferrer">
            <Icon name="pin" size={13} /> Mappa
          </a>
        )}
        {(i.place?.booking_url || i.place?.website) && (
          <a className="act" href={i.place.booking_url ?? i.place.website}
             target="_blank" rel="noreferrer">
            {i.place.booking_url ? 'Prenota' : 'Sito'}
          </a>
        )}
        {chosen
          ? (
            <button className="act quiet" disabled={busy}
                    onClick={() => act(() => unchooseOption(i.id))}>
              {busy ? 'Attendi…' : 'Rimetti in valutazione'}
            </button>
          )
          : (
            <>
              <button className="act strong" disabled={busy}
                      onClick={() => setPicking((v) => !v)}>
                <Icon name="check" size={13} /> Lo facciamo
              </button>
              {/* Scartare is the other half of deciding, and without it a list
                  only ever grows. The row is kept, not deleted: "we looked at
                  this and said no" is worth being able to read in three weeks,
                  and worth being able to take back. */}
              <button className="act quiet" disabled={busy}
                      onClick={() => act(() => setOptionStatus(i.id, 'rejected'))}>
                {busy ? 'Attendi…' : 'Scarta'}
              </button>
            </>
          )}
      </div>

      {picking && !chosen && (
        <div className="pickday">
          <p className="q">Quale giorno?</p>
          <div className="days">
            {days.map((d) => (
              <button key={d} className={`daychip${day === d ? ' on' : ''}`}
                      onClick={() => setDay(d)}>
                {dayShort(d)}
              </button>
            ))}
            <button className={`daychip${day === '' ? ' on' : ''}`} onClick={() => setDay('')}>
              senza data
            </button>
          </div>
          <div className="when">
            <label>
              Ora <span>facoltativa</span>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </label>
            <button className="btn primary" disabled={busy || day === undefined}
                    onClick={() => act(async () => {
                      await chooseOption(i.id, {
                        event_date: day || null,
                        starts_at: isoInZone(day, time, stopZone),
                        timezone: time ? stopZone : null,
                        time_note: !time ? null : undefined,
                      });
                      setPicking(false);
                    })}>
              {busy ? 'Confermo…' : 'Conferma'}
            </button>
          </div>
          {day === undefined && <p className="hint">Scegli un giorno per confermare.</p>}
          {day === '' && (
            <p className="hint">Senza data finisce in «Da collocare», non su un giorno.</p>
          )}
        </div>
      )}
      {!hideFrom && sl?.title && <p className="from">da «{sl.title}»</p>}
    </article>
  );
}

// A decision and its discarded alternatives, with the reasoning that separated
// them. Shared between a stop and the trip, because a choice that names no stop
// still has to be somewhere: filing these by stop alone is what made one of them
// disappear from the app entirely.
function Shortlist({ list: sl, onChange }) {
  const chosen = sl.items.filter((i) => i.status === 'chosen');
  const candidates = sl.items.filter((i) => i.status === 'candidate');
  const rejected = sl.items.filter((i) => i.status === 'rejected');
  const rest = sl.items.filter((i) =>
    !['chosen', 'candidate', 'rejected'].includes(i.status));

  return (
    <section className="section">
      <h2>{sl.title} <span className="count">
        {sl.decided ? 'deciso' : `${candidates.length} da valutare`}
      </span></h2>
      {sl.criteria && <p className="lead">{sl.criteria}</p>}
      <div className="rows">
        {[...chosen, ...candidates, ...rejected, ...rest].map((i) => (
          <div className={`row option ${i.status}`} key={i.id}>
            <span className="body">
              <b>{i.title ?? i.place?.name}</b>
              {i.distance_note && <small>{i.distance_note}</small>}
              {i.verdict && <small>{i.verdict}</small>}
              {i.pros?.length > 0 && <small style={{ color: 'var(--ok)' }}>+ {i.pros.join(' · ')}</small>}
              {i.cons?.length > 0 && <small style={{ color: 'var(--attn)' }}>− {i.cons.join(' · ')}</small>}
              {(i.place?.phone || isMappable(i.place) || i.vendor?.whatsapp) && (
                <span className="actions">
                  {(i.place?.phone || i.vendor?.whatsapp) && (
                    <a className="act" href={telHref(i.place?.phone ?? i.vendor?.whatsapp)}>
                      <Icon name="phone" size={13} /> Chiama
                    </a>
                  )}
                  {isMappable(i.place) && (
                    <a className="act" href={mapsHref(i.place)} target="_blank" rel="noreferrer">
                      <Icon name="pin" size={13} /> Mappa
                    </a>
                  )}
                </span>
              )}
            </span>
            <span className="trail">
              <span className={`state ${i.status === 'chosen' ? 'ok'
                : i.status === 'rejected' ? 'muted' : 'attn'}`}>
                {i.status === 'chosen' ? 'scelto'
                  : i.status === 'rejected' ? 'scartato'
                  : i.status === 'booked' ? 'prenotato' : 'da valutare'}
              </span>
              {i.est_total_local != null && (
                <small>{localAmount(i.est_total_local, i.est_currency_local)}</small>
              )}
              {i.reservation_status === 'to_book' && <small>da prenotare</small>}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
